import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutGrid, FolderKanban, Inbox, Users, Plug, Settings, ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "@/platform/AuthContext";
import { Logo, ACCENT } from "@/platform/components/primitives";
import { api } from "@/platform/api";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/app/projects", label: "Projects", icon: FolderKanban },
  { to: "/app/inbox", label: "Inbox", icon: Inbox },
  { to: "/app/team", label: "Team", icon: Users },
  { to: "/app/integrations", label: "Integrations", icon: Plug },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function initials(name) {
  return (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}
function trialDays(sub) {
  if (!sub?.trialEndsAt) return null;
  const d = Math.ceil((new Date(sub.trialEndsAt).getTime() - Date.now()) / 86400000);
  return d > 0 ? d : 0;
}

export default function AppLayout() {
  const navigate = useNavigate();
  const { user, workspace, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const menuRef = useRef(null);

  useEffect(() => {
    let stop = false;
    const fetchUnread = async () => {
      try { const r = await api.get("/api/notifications/unread-count"); if (!stop) setUnread(r.count || 0); } catch (_e) { /* ignore */ }
    };
    fetchUnread();
    const i = setInterval(fetchUnread, 20000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    window.addEventListener("pinlayr:notif", fetchUnread);
    return () => { stop = true; clearInterval(i); window.removeEventListener("focus", onFocus); window.removeEventListener("pinlayr:notif", fetchUnread); };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (user && !workspace) return <Navigate to="/workspace" replace />;

  const wsName = workspace?.workspace?.name || "Workspace";
  const days = trialDays(workspace?.subscription);
  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#0E1016]">
      <header className="sticky top-0 z-30 border-b border-[#242A38] bg-[#151A24]">
        <div className="flex h-16 items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="mx-1 h-5 w-px bg-[#242A38]" />
            <div className="flex items-center gap-2 text-[14px] font-medium text-[#C4C9D4]" data-testid="workspace-name">
              <span className="grid h-5 w-5 place-items-center rounded text-[10px] font-bold" style={{ background: ACCENT, color: "#171717" }}>{initials(wsName)}</span>
              {wsName}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {days !== null && (
              <span className="hidden rounded-full bg-[#6D5EF6] px-3 py-1.5 text-[12px] font-medium text-white sm:inline-block" data-testid="trial-badge">{days} {days === 1 ? "day" : "days"} left in trial</span>
            )}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-[#1C2130]" data-testid="user-menu">
                {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="grid h-8 w-8 place-items-center rounded-full bg-[#6D5EF6] text-[12px] font-semibold text-white">{initials(user?.fullName)}</span>}
                <ChevronDown className="h-4 w-4 text-[#6E7686]" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border border-[#242A38] bg-[#151A24] shadow-lg" data-testid="user-menu-dropdown">
                  <div className="border-b border-[#242A38] px-4 py-3">
                    <p className="truncate text-[14px] font-semibold text-[#F4F5F8]">{user?.fullName}</p>
                    <p className="truncate text-[12.5px] text-[#6E7686]">{user?.email}</p>
                  </div>
                  <button onClick={doLogout} className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-[14px] text-[#C4C9D4] hover:bg-[#0E1016]" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Log out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-56 shrink-0 border-r border-[#242A38] bg-[#151A24] p-3 md:block">
          <nav className="space-y-0.5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${isActive ? "bg-[#1C2130] text-[#F4F5F8]" : "text-[#98A0B0] hover:bg-[#0E1016] hover:text-[#F4F5F8]"}`}>
                <n.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{n.label}</span>
                {n.label === "Inbox" && unread > 0 && (
                  <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-[#6D5EF6] px-1.5 text-[11px] font-bold text-white" data-testid="inbox-badge">{unread > 99 ? "99+" : unread}</span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 px-5 py-8 sm:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
